import React, { useEffect, useMemo, useRef, useState } from "react";

import { LazyQueryExecFunction } from "@apollo/client";
import {
  Attribute,
  ByProjectIdReqInput,
  Exact,
  MdmDeviceDto,
  MdmObjectDto,
  SortDirection,
  TablesDetailsInput,
  useUpdateTableDetailsForUserMutation,
  useUpdateUserSettingsMutation,
} from "gql/generated/types";
import { Button } from "primereact/button";
import { Column } from "primereact/column";
import { DataTable, DataTablePFSEvent } from "primereact/datatable";
import { Toolbar } from "primereact/toolbar";
import { useNavigate } from "react-router-dom";
import { TableColumn } from "utils/types";
import { exportExcel, findObjectVal } from "utils/utils";
import useProjectContext from "../hooks/useProjectContext";
import { LazyState } from "../utils/types";
import { errorToaster, successToaster } from "../utils/utils";
import TableColumnSelection from "./TableColumnSelection";
import TableFilters from "./TableFilters";
import { DEVICES_TABLE_NAME, DEVICE_CONTAINER, OBJECTS_TABLE_NAME, OBJECT_CONTAINER } from "../utils/const";

type CustomFieldType = Record<
  string,
  {
    value: string | null | undefined;
    id: string | null | undefined;
    title: string | undefined;
  }
>;

type MainTableProps<T, N> = {
  data: Partial<T>[];
  tableName: typeof DEVICES_TABLE_NAME | typeof OBJECTS_TABLE_NAME;
  columns: TableColumn<Partial<T>>[];
  loading: boolean;
  onLazyEvent: React.Dispatch<React.SetStateAction<LazyState>>;
  lazyState: LazyState;
  totalItems: number;
  getNextQuery: LazyQueryExecFunction<N, Exact<{ input: ByProjectIdReqInput }>>;
};

const LazyTable = <T extends MdmDeviceDto | MdmObjectDto, N>({
  data,
  tableName,
  columns,
  loading,
  onLazyEvent,
  lazyState,
  totalItems,
  getNextQuery,
}: MainTableProps<T, N>) => {
  const [excelData, setExcelData] = useState<Partial<T>[]>([]);
  const [viewFilters, setViewFilters] = useState(false);
  const [customColumns, setCustomColumns] = useState<string[]>([]);

  const dt = useRef<DataTable>(null);
  const navigate = useNavigate();
  const { user, userSettings, project, projectId } = useProjectContext();

  let columnList: string[] = [];
  let customCol: Attribute[] = [];

  if (tableName === DEVICES_TABLE_NAME) {
    columnList = (userSettings?.userSettings.deviceTable as string[]) ?? [];
    customCol = [
      ...(project?.mdmProjectById.configuration?.deviceConfiguration.customFields ?? []),
    ] as Attribute[];
  } else if (tableName === OBJECTS_TABLE_NAME) {
    columnList = (userSettings?.userSettings.objectTable as string[]) ?? [];
    customCol = [
      ...(project?.mdmProjectById.configuration?.objectConfiguration.customFields ?? []),
    ] as Attribute[];
  }

  const userTableDetails = () =>
    userSettings?.userSettings.tablesDetails?.find(r => r?.table === tableName);

  useEffect(() => {
    if (userSettings && columnList.length > 0) {
      setCustomColumns(columnList);
    } else {
      setCustomColumns(columns.map(col => col.header));
    }
    if (userTableDetails()?.rows !== lazyState?.rows) {
      onLazyEvent(prev => ({ ...prev, rows: userTableDetails()?.rows || 10 }));
    }
  }, [userSettings]);

  useEffect(() => {
    if (data && data.length > 0) {
      setExcelData(data);
      dt.current?.reset();
    }
  }, [data]);

  const [updateTableDetails] = useUpdateTableDetailsForUserMutation();
  const [updateUserSettings, { loading: updateLoading }] = useUpdateUserSettingsMutation({
    onCompleted: () => successToaster("Columns saved successfully"),
    onError: errorToaster,
  });

  const mapCol = useMemo(
    () =>
      [
        ...columns,
        ...(customCol?.map(col => ({
          field: `${col?.id}.value`,
          header: col?.title ?? "",
          sortable: true,
        })) ?? []),
      ] as TableColumn<T>[],
    [columns]
  );

  const formatObjectFields = data.reduce((acc, obj) => {
    const objFields = obj.customFields?.reduce((accObj, field) => {
      const newObj = {
        value: field?.value?.value,
        id: field?.key,
        title: customCol.find(col => col?.id === field?.key)?.title,
      };
      return {
        ...accObj,
        [field?.key!]: newObj,
      };
    }, {} as CustomFieldType);

    acc.push({
      ...obj,
      ...objFields,
    });

    return acc;
  }, [] as Partial<T>[]);

  const filteredColumns = useMemo(
    () => mapCol.filter(col => customColumns.includes(col.header)),
    [customColumns, mapCol]
  );

  const handleExportExcel = () => {
    const tableData = excelData.map(row => {
      const newData: any = {};

      columns.forEach(col => {
        if (col.hideInFile || col.hidden) return;
        newData[col.header] = col.field ? findObjectVal(row, col.field)?.toString() : col.body;
      });
      return newData;
    });

    exportExcel(tableData, tableName);
  };

  const exportCSV = () => {
    dt.current?.exportCSV({ selectionOnly: false });
  };

  const handleColumnSave = () => {
    if (tableName === DEVICES_TABLE_NAME) {
      updateUserSettings({
        variables: {
          userSettings: {
            id: user?.externalId!,
            deviceTable: customColumns,
          },
        },
      });
      return;
    }
    updateUserSettings({
      variables: {
        userSettings: {
          id: user?.externalId!,
          objectTable: customColumns,
        },
      },
    });
  };

  const onPage = (e: DataTablePFSEvent) => {
    const tableDetails: TablesDetailsInput = {
      table: tableName,
      rows: e.rows,
      columnOrders: userTableDetails()?.columnOrders || [],
    };
    if (userTableDetails()?.rows !== e.rows) {
      updateTableDetails({
        variables: { tableDetails },
      });
    }
    onLazyEvent(prev => ({ ...prev, first: e.first, page: e.page }));
    fetchData(e, e.rows, e.first);
  };

  const onSort = (e: DataTablePFSEvent) => {
    onLazyEvent(prev => ({ ...prev, sortField: e.sortField, sortOrder: e.sortOrder, first: 0 }));
    fetchData(e, userTableDetails()?.rows);
  };

  const fetchData = (e: DataTablePFSEvent, take: number = 10, skip: number = 0) => {
    const properties = e.sortField.split(".");
    getNextQuery({
      variables: {
        input: {
          projectId,
          skip,
          take,
          sort: [
            {
              property:
                properties.length === 2 && properties.at(-1) === "value"
                  ? `customFields.${properties[0]}`
                  : e.sortField!,
              direction: e.sortOrder === 1 ? SortDirection.Asc : SortDirection.Desc,
            },
          ],
          andFilters: lazyState.andFilters,
        },
      },
    });
  };

  return (
    <div className="card p-0 sm:p-3">
      <Toolbar
        left={
          <div className="flex gap-3">
            <Button
              label="Filters"
              icon={viewFilters ? "pi pi-chevron-up" : "pi pi-chevron-down"}
              onClick={() => setViewFilters(prev => !prev)}
            />
          </div>
        }
        right={
          <div className="flex gap-3">
            <Button
              type="button"
              icon="pi pi-file"
              className="p-button-outlined p-button-warning"
              title="Export to CSV"
              onClick={exportCSV}
            />
            <Button
              type="button"
              icon="pi pi-file-excel"
              className="p-button-outlined p-button-warning"
              title="Export to Excel"
              onClick={handleExportExcel}
            />
            <TableColumnSelection
              customColumns={customColumns}
              setCustomColumns={setCustomColumns}
              handleColumnSave={handleColumnSave}
              mapCol={mapCol}
              loading={updateLoading}
            />
            <Button
              type="button"
              icon="pi pi-plus"
              label={tableName === DEVICES_TABLE_NAME ? "Add Device" : "Add Object"}
              onClick={() => navigate(tableName === DEVICES_TABLE_NAME ? "new" : "objects/new")}
            />
          </div>
        }
        className="gap-3"
      />

      <DataTable
        header={
          <div
            className={`${viewFilters ? "max-h-90rem" : "max-h-0"} overflow-hidden transition-all transition-duration-500 p-0`}
          >
            <TableFilters
              container={tableName === DEVICES_TABLE_NAME ? DEVICE_CONTAINER : OBJECT_CONTAINER}
              getNextQuery={getNextQuery}
              lazyState={lazyState}
              onLazyEvent={onLazyEvent}
            />
          </div>
        }
        ref={dt}
        value={formatObjectFields}
        onValueChange={tableData => {
          setExcelData(tableData);
        }}
        stripedRows
        paginator
        rows={lazyState.rows}
        reorderableColumns
        rowsPerPageOptions={[5, 10, 25, 50]}
        paginatorTemplate="RowsPerPageDropdown FirstPageLink PrevPageLink CurrentPageReport NextPageLink LastPageLink"
        currentPageReportTemplate="{first} to {last} of {totalRecords}"
        loading={loading}
        size="small"
        lazy
        totalRecords={totalItems}
        className="overflow-x-auto lazy-table"
        first={lazyState.first || 0}
        sortField={lazyState.sortField}
        sortOrder={lazyState.sortOrder}
        onPage={onPage}
        onSort={onSort}
      >
        {filteredColumns.map(col => (
          <Column key={col.header} {...col} />
        ))}
      </DataTable>
    </div>
  );
};

export default LazyTable;
